import { Injectable, PLATFORM_ID, inject } from '@angular/core';
import { isPlatformBrowser } from '@angular/common';
import { LocalStorageService } from '../local-storage.service';
import { LOCALSTORAGE_KEYS } from '../constants/databases';

/**
 * Query params worth attributing, mapped to the Klaviyo custom profile property
 * they land on. Klaviyo creates these on the fly, so adding a row here is all it
 * takes to start sending a new one — no setup on the Klaviyo side.
 */
const TRACKED_PARAMS: Record<string, string> = {
  utm_source: 'UTM Source',
  utm_medium: 'UTM Medium',
  utm_campaign: 'UTM Campaign',
  utm_term: 'UTM Term',
  utm_content: 'UTM Content',
  gclid: 'Google Click ID',
  fbclid: 'Facebook Click ID',
};

/** Tracked query param → its name in the `attribution` body the backend receives (DEV-11429). */
const PAYLOAD_FIELDS: Record<string, keyof AttributionTouch> = {
  utm_source: 'utmSource',
  utm_medium: 'utmMedium',
  utm_campaign: 'utmCampaign',
  utm_term: 'utmTerm',
  utm_content: 'utmContent',
  gclid: 'gclid',
  fbclid: 'fbclid',
};

/** Longest URL we send to the backend; landing pages with tracking junk can run long. */
const MAX_URL_LENGTH = 2048;

export interface FirstTouchAttribution {
  /** Tracked query params as they arrived on the landing URL. */
  params: Record<string, string>;
  /** External `document.referrer`; '' for direct traffic or a same-origin entry. */
  referringUrl: string;
  /** Full URL of the first page of the visit, query string included. */
  landingPage: string;
  capturedAt: string;
}

/** The latest touch is stored in the same shape as the first one. */
export type LatestTouchAttribution = FirstTouchAttribution;

/** One touch as the backend receives it (proposed contract for DEV-11428). */
export interface AttributionTouch {
  utmSource?: string;
  utmMedium?: string;
  utmCampaign?: string;
  utmTerm?: string;
  utmContent?: string;
  gclid?: string;
  fbclid?: string;
  referrer?: string;
  landingPage?: string;
  capturedAt: string;
}

/** The `attribution` key sent in POST /account, POST /login and POST/PATCH /order. */
export interface AttributionPayload {
  firstTouch?: AttributionTouch;
  latestTouch?: AttributionTouch;
}

/**
 * First-touch (DEV-10930) and latest-touch (DEV-11429) traffic attribution.
 *
 * Deliberately knows nothing about Klaviyo, the signup flow or the coupon
 * machinery in `checkMarketingReferral`: UTMs and `document.referrer` only exist
 * in the browser, so whoever ends up consuming them, the capture has to happen
 * here. Keeping this file free of brand-specific dependencies is what makes it
 * portable as-is to the other brand branches, whose KlaviyoService variants and
 * signup flows diverge.
 *
 * Note this is NOT the same thing as LOCALSTORAGE_KEYS.MARKETING_REFERRAL. That
 * one is a coupon mechanism — it only persists when all three utm_* params are
 * present AND the campaign matches a real promotion — so it drops the ordinary
 * ad traffic this service exists to record.
 */
@Injectable({ providedIn: 'root' })
export class AttributionService {
  #localStorageService = inject(LocalStorageService);
  #platformId = inject(PLATFORM_ID);

  /**
   * Record the traffic source of this visit. Safe to call on every app boot:
   * - the first touch is never overwritten, so a visitor who arrives from a
   *   campaign, leaves, and comes back directly days later still signs up
   *   credited to the campaign;
   * - the latest touch is replaced only by a visit that carries at least one
   *   utm_* param. Direct, referrer-only and click-id-only (gclid/fbclid)
   *   visits leave it alone, and so does a reload of the same tagged landing,
   *   so its capturedAt stays put.
   *
   * Only call it once per boot: internal SPA navigation is not a new touch.
   *
   * Cannot throw. Callers run this from AppComponent's constructor, so anything
   * escaping would take the whole app down before it paints — and persisting is
   * the risky part: `localStorage.setItem` throws on a full quota, in private
   * modes and in Safari with Block All Cookies, and not every brand's
   * LocalStorageService guards its writes. That exact failure took a signup
   * funnel down once already (DEV-11068). Attribution is fire-and-forget:
   * losing it must never cost us the page.
   */
  capture(): void {
    if (!isPlatformBrowser(this.#platformId)) return;

    try {
      this.#capture();
    } catch (error) {
      console.warn('Attribution: could not record the visit.', error);
    }
  }

  #capture(): void {
    const search = new URLSearchParams(window.location.search);
    const params: Record<string, string> = {};
    for (const key of Object.keys(TRACKED_PARAMS)) {
      const value = search.get(key)?.trim();
      if (value) params[key] = value;
    }

    const referringUrl = this.#externalReferrer();

    // Direct traffic with no campaign tags: there is nothing to attribute, and
    // storing an empty record would make this visit the "first touch" and lock
    // out a later tagged one. Leave the slot empty instead.
    if (!referringUrl && !Object.keys(params).length) return;

    const attribution: FirstTouchAttribution = {
      params,
      referringUrl,
      landingPage: window.location.href,
      capturedAt: new Date().toISOString(),
    };

    // Independent writes: losing one slot must not cost us the other.
    try {
      if (!this.#read()) this.#localStorageService.set(LOCALSTORAGE_KEYS.FIRST_TOUCH_ATTRIBUTION, attribution);
    } catch (error) {
      console.warn('Attribution: could not record the first touch.', error);
    }

    try {
      this.#saveLatestTouch(attribution);
    } catch (error) {
      console.warn('Attribution: could not record the latest touch.', error);
    }
  }

  /**
   * The latest touch is the whole set of params of the last UTM-tagged visit,
   * never a field-by-field merge: crediting one campaign's utm_content to
   * another's utm_campaign would be false data.
   *
   * A visit needs a utm_* param to count. The backend ignores gclid/fbclid, so
   * a click-id-only visit would reach it as a touch with no source at all and
   * wipe out the campaign we had. Click ids still ride along when they come
   * with UTMs.
   */
  #saveLatestTouch(attribution: LatestTouchAttribution): void {
    if (!Object.keys(attribution.params).some(key => key.startsWith('utm_'))) return;

    const current = this.#readLatest();
    if (current && sameParams(current.params, attribution.params)) return;

    this.#localStorageService.set(LOCALSTORAGE_KEYS.LATEST_TOUCH_ATTRIBUTION, attribution);
  }

  /**
   * The captured attribution as Klaviyo custom profile properties, ready to
   * spread into a `createProfile` / event `properties` payload. Every value is a
   * string so this also fits the narrower `Record<string, string>` signature the
   * other branches use.
   *
   * Cannot throw, for the same reason capture() cannot but with more at stake:
   * this one is read from inside createProfile's promise, which the signup
   * awaits before it lets you leave step 1. A rejection there would surface to
   * the customer as a Next button that does nothing. Reading a record someone
   * else's code wrote is not worth that risk, so a bad one yields no properties.
   *
   * @returns an empty object when nothing was captured, so spreading it is a no-op.
   */
  getProperties(): Record<string, string> {
    try {
      return this.#properties();
    } catch (error) {
      console.warn('Attribution: could not read the first touch.', error);
      return {};
    }
  }

  #properties(): Record<string, string> {
    const stored = this.#read();
    if (!stored) return {};

    const properties: Record<string, string> = {};

    for (const [param, label] of Object.entries(TRACKED_PARAMS)) {
      const value = stored.params?.[param];
      if (value) properties[label] = value;
    }

    if (stored.referringUrl) properties['Referring URL'] = stored.referringUrl;
    if (stored.landingPage) properties['Landing Page'] = stored.landingPage;
    if (stored.capturedAt) properties['First Touch At'] = stored.capturedAt;

    return properties;
  }

  /** The raw captured record, for consumers that need it unmapped. */
  getAttribution(): FirstTouchAttribution | null {
    return this.#read();
  }

  /**
   * Both touches in the shape the backend receives them, or null when nothing
   * was captured. The front sends whatever it has on every call and the backend
   * decides (first touch: set-if-empty; latest touch: newer capturedAt wins), so
   * there is no "already sent" flag to keep in sync here.
   *
   * Cannot throw: it runs while building the signup, login and order bodies.
   */
  getPayload(): AttributionPayload | null {
    try {
      const firstTouch = toPayloadTouch(this.#read());
      const latestTouch = toPayloadTouch(this.#readLatest());
      if (!firstTouch && !latestTouch) return null;

      return {
        ...(firstTouch && { firstTouch }),
        ...(latestTouch && { latestTouch }),
      };
    } catch (error) {
      console.warn('Attribution: could not build the payload.', error);
      return null;
    }
  }

  /** `body` plus an `attribution` key when there is something to send; `body` untouched otherwise. */
  withAttribution<T extends object>(body: T): T & { attribution?: AttributionPayload } {
    const attribution = this.getPayload();
    return attribution ? { ...body, attribution } : body;
  }

  /**
   * `document.referrer` filtered down to real traffic sources. A same-origin
   * referrer is internal navigation — a reload, or a link from another page of
   * the site — not where the visitor came from.
   *
   * Expect this to be empty often: direct traffic has no referrer, in-app
   * browsers (Instagram, email clients) send none, and Google and Meta send only
   * their origin under their own Referrer-Policy. The UTMs are the reliable half.
   */
  #externalReferrer(): string {
    const referrer = document.referrer;
    if (!referrer) return '';

    try {
      if (new URL(referrer).origin === window.location.origin) return '';
    } catch {
      return '';
    }

    return referrer;
  }

  #read(): FirstTouchAttribution | null {
    if (!isPlatformBrowser(this.#platformId)) return null;

    try {
      return this.#localStorageService.get<FirstTouchAttribution>(LOCALSTORAGE_KEYS.FIRST_TOUCH_ATTRIBUTION);
    } catch {
      // LocalStorageService AES-decrypts, which throws on a value written under
      // a different crypto key. Treat it as "nothing captured" and move on.
      return null;
    }
  }

  #readLatest(): LatestTouchAttribution | null {
    if (!isPlatformBrowser(this.#platformId)) return null;

    try {
      return this.#localStorageService.get<LatestTouchAttribution>(LOCALSTORAGE_KEYS.LATEST_TOUCH_ATTRIBUTION);
    } catch {
      return null;
    }
  }
}

function sameParams(a: Record<string, string> | undefined, b: Record<string, string>): boolean {
  const keys = Object.keys(b);
  return !!a && Object.keys(a).length === keys.length && keys.every(key => a[key] === b[key]);
}

/** A stored record mapped onto the contract; null for nothing, or for a record too broken to date. */
function toPayloadTouch(stored: FirstTouchAttribution | null): AttributionTouch | null {
  if (!stored?.capturedAt) return null;

  const touch: AttributionTouch = { capturedAt: stored.capturedAt };

  for (const [param, field] of Object.entries(PAYLOAD_FIELDS)) {
    const value = stored.params?.[param];
    if (value) (touch as unknown as Record<string, string>)[field] = value;
  }

  if (stored.referringUrl) touch.referrer = stored.referringUrl.slice(0, MAX_URL_LENGTH);
  if (stored.landingPage) touch.landingPage = stored.landingPage.slice(0, MAX_URL_LENGTH);

  return touch;
}
